/**
 * Finances (F3, `finances` capability, church scope): income (offerings/
 * tithes), expenses, funds/line items and categories; money ALWAYS in minor
 * units (the decimal conversion lives on the input/export boundary);
 * attached receipt (real storage); report by range/fund/category and
 * K1 export (CSV RFC-4180 with anti-injection + printable PDF).
 *
 * Local SQLite ONLY contains finances if the account has `finances:read`
 * (isolation tested in F0b/F1 and in apps/server/tests/administration.rs);
 * this page does not re-filter visibility, it only gates ACTIONS with the grants.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { EXPORT_DELIVERY } from "@fleet/plugins/export";
import { useKernelService } from "@fleet/kernel";
import {
  buildFinanceDataset,
  exportDataset,
  type ExportFormat,
  filterFinanceRecords,
  financeSummary,
  formatMoney,
  getChurchesQuery,
  getFinanceCategoriesQuery,
  getFinanceRecordsQuery,
  getFundsQuery,
  minorToDecimal,
  parseDecimalToMinor,
  patchRowVerified,
  queryKeys,
} from "@agape/shared";
import {
  type ChurchRecord,
  type FinanceCategoryRecord,
  finance_category as financeCategoryTable,
  type FinanceRecordRecord,
  finance_record as financeRecordTable,
  type FundRecord,
  fund as fundTable,
} from "@agape/powersync";
import { Banknote, Download, FileText, Paperclip, Plus, Printer, Trash2 } from "@fleet/icons";

import { resolveStorageUrl } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog, UploadField } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function FinancesPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const records = useDomainQuery<FinanceRecordRecord>(
    (uid) => queryKeys.domain.financeRecords(uid),
    (db) => getFinanceRecordsQuery(db),
  );
  const funds = useDomainQuery<FundRecord>(
    (uid) => queryKeys.domain.funds(uid),
    (db) => getFundsQuery(db),
  );
  const categories = useDomainQuery<FinanceCategoryRecord>(
    (uid) => queryKeys.domain.financeCategories(uid),
    (db) => getFinanceCategoriesQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );

  // Churches with readable/writable finances (same grants as the sync).
  const financeChurches = createMemo(() =>
    (churches.data ?? []).filter((c) => grants.can([c.id], "finances", "read")),
  );
  const [churchId, setChurchId] = createSignal("");
  const activeChurch = () => churchId() || financeChurches()[0]?.id || "";

  const churchFunds = createMemo(() =>
    (funds.data ?? []).filter((f) => f.church_id === activeChurch()),
  );
  const churchCategories = createMemo(() =>
    (categories.data ?? []).filter((c) => c.church_id === activeChurch()),
  );
  const fundName = (id: string | null) => churchFunds().find((f) => f.id === id)?.name ?? "";
  const categoryName = (id: string | null) =>
    churchCategories().find((c) => c.id === id)?.name ?? "";

  // ── Report filter (range + fund + category) ───────────────────────────────
  const [fromDate, setFromDate] = createSignal("");
  const [toDate, setToDate] = createSignal("");
  const [filterFund, setFilterFund] = createSignal("");
  const [filterCategory, setFilterCategory] = createSignal("");

  const filter = createMemo(() => ({
    churchId: activeChurch() || null,
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- ISO suffix of the range, not UI
    fromMs: fromDate() ? Date.parse(`${fromDate()}T00:00:00Z`) : null,
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- ISO suffix of the range, not UI
    toMs: toDate() ? Date.parse(`${toDate()}T23:59:59.999Z`) : null,
    fundId: filterFund() || null,
    categoryId: filterCategory() || null,
  }));
  const filtered = createMemo(() => filterFinanceRecords(records.data ?? [], filter()));
  const summary = createMemo(() => financeSummary(filtered()));
  const mainCurrency = () => filtered()[0]?.currency ?? "EUR";

  // ── New record (income/expense) ───────────────────────────────────────────
  const [open, setOpen] = createSignal(false);
  const [kind, setKind] = createSignal<string>("income");
  const [description, setDescription] = createSignal("");
  const [amount, setAmount] = createSignal("");
  const [currency, setCurrency] = createSignal("EUR");
  const [occurredAt, setOccurredAt] = createSignal(new Date().toISOString().slice(0, 10));
  const [fundId, setFundId] = createSignal("");
  const [categoryId, setCategoryId] = createSignal("");
  const [receiptUrl, setReceiptUrl] = createSignal("");

  const amountMinor = createMemo(() => parseDecimalToMinor(amount(), currency()));
  const amountInvalid = () => amount().trim() !== "" && amountMinor() === null;

  const createRecord = async () => {
    const minor = amountMinor();
    const church = activeChurch();
    if (!description().trim() || minor === null || minor <= 0 || !church) {
      showToast(t(msg`Enter a description and a valid positive amount`), "error");
      return;
    }
    await write()
      .insert(financeRecordTable)
      .values({
        id: newId(),
        church_id: church,
        kind: kind(),
        description: description().trim(),
        amount_minor: minor,
        currency: currency().trim().toUpperCase() || "EUR",
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- ISO suffix of the date, not UI
        occurred_at: occurredAt() ? `${occurredAt()}T12:00:00.000Z` : nowIso(),
        fund_id: fundId() || null,
        category_id: categoryId() || null,
        receipt_url: receiptUrl() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setOpen(false);
    setDescription("");
    setAmount("");
    setReceiptUrl("");
  };

  const trashRecord = async (id: string) => {
    // Financial (F3): a phantom write here is money that stops reconciling
    // in the ledger — it re-reads and retries (`patchRowVerified`) and, if it
    // really does not land, it warns instead of letting it vanish silently.
    try {
      await patchRowVerified(write(), financeRecordTable, financeRecordTable.id, id, {
        deleted_at: nowIso(),
      });
    } catch {
      showToast(t(msg`Couldn't move the record to trash — try again`), "error");
    }
  };

  // ── Funds and categories ──────────────────────────────────────────────────
  const [fundOpen, setFundOpen] = createSignal(false);
  const [fundNameInput, setFundNameInput] = createSignal("");
  const createFund = async () => {
    if (!fundNameInput().trim() || !activeChurch()) {
      return;
    }
    await write().insert(fundTable).values({
      id: newId(),
      church_id: activeChurch(),
      name: fundNameInput().trim(),
      sort_order: churchFunds().length,
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setFundOpen(false);
    setFundNameInput("");
  };

  const [categoryOpen, setCategoryOpen] = createSignal(false);
  const [categoryNameInput, setCategoryNameInput] = createSignal("");
  const [categoryKind, setCategoryKind] = createSignal("both");
  const createCategory = async () => {
    if (!categoryNameInput().trim() || !activeChurch()) {
      return;
    }
    await write().insert(financeCategoryTable).values({
      id: newId(),
      church_id: activeChurch(),
      name: categoryNameInput().trim(),
      kind: categoryKind(),
      sort_order: churchCategories().length,
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setCategoryOpen(false);
    setCategoryNameInput("");
  };

  // ── Export K1 ─────────────────────────────────────────────────────────────
  const delivery = useKernelService(EXPORT_DELIVERY);

  const getExportDataset = () => {
    const church = financeChurches().find((c) => c.id === activeChurch());
    return buildFinanceDataset(
      filtered().map((r) => ({
        ...r,
        amount_minor: r.amount_minor ?? 0,
        fundName: fundName(r.fund_id),
        categoryName: categoryName(r.category_id),
      })),
      { subtitle: church?.name ?? undefined },
    );
  };

  const handleExport = (format: ExportFormat) => {
    const target = delivery();
    if (!target) {
      return;
    }
    void exportDataset(getExportDataset(), format, target);
  };

  const canWrite = () => grants.can([activeChurch()], "finances", "write");

  const kindLabel = (k: string | null) => (k === "expense" ? t(msg`Expense`) : t(msg`Income`));

  return (
    <div class="space-y-4" data-testid="finances-page">
      <PageSubHeader title={t(msg`Finances`)} />
      <Show
        when={financeChurches().length > 0}
        fallback={
          <p data-testid="finances-empty" class="text-muted-foreground">
            {t(msg`No finances synced to this account. Ask a manager for the finances capability.`)}
          </p>
        }
      >
        <div class="flex flex-wrap items-end gap-3">
          <div class="min-w-44">
            <Select
              options={financeChurches().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
              value={activeChurch()}
              onChange={(v) => setChurchId(v)}
              aria-label={t(msg`Church`)}
              data-testid="finances-church-select"
            />
          </div>
          <div class="ml-auto flex flex-wrap gap-2">
            <Show when={canWrite()}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFundOpen(true)}
                data-testid="fund-add"
              >
                <Plus size={14} /> {t(msg`Fund`)}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCategoryOpen(true)}
                data-testid="finance-category-add"
              >
                <Plus size={14} /> {t(msg`Category`)}
              </Button>
              <Button size="sm" onClick={() => setOpen(true)} data-testid="finance-add">
                <Plus size={14} /> {t(msg`Record`)}
              </Button>
            </Show>
          </div>
        </div>

        {/* Resumen del reporte */}
        <div class="grid grid-cols-3 gap-3">
          <Card>
            <CardContent class="py-3">
              <p class="text-xs uppercase tracking-wider text-muted-foreground">{t(msg`Income`)}</p>
              <p class="text-lg font-bold text-primary" data-testid="finance-income-total">
                {formatMoney(summary().incomeMinor, mainCurrency())}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent class="py-3">
              <p class="text-xs uppercase tracking-wider text-muted-foreground">
                {t(msg`Expenses`)}
              </p>
              <p class="text-lg font-bold text-destructive" data-testid="finance-expense-total">
                {formatMoney(summary().expenseMinor, mainCurrency())}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent class="py-3">
              <p class="text-xs uppercase tracking-wider text-muted-foreground">{t(msg`Net`)}</p>
              <p class="text-lg font-bold" data-testid="finance-net-total">
                {formatMoney(summary().netMinor, mainCurrency())}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filtros del reporte + export */}
        <div class="flex flex-wrap items-end gap-3">
          <Field
            id="fin-from"
            label={t(msg`From`)}
            type="date"
            value={fromDate()}
            onInput={setFromDate}
            testId="finance-filter-from"
          />
          <Field
            id="fin-to"
            label={t(msg`To`)}
            type="date"
            value={toDate()}
            onInput={setToDate}
            testId="finance-filter-to"
          />
          <Field id="fin-fund" label={t(msg`Fund`)}>
            <Select
              options={[
                { value: "", label: t(msg`All funds`) },
                ...churchFunds().map((f) => ({ value: f.id, label: f.name ?? f.id })),
              ]}
              value={filterFund()}
              onChange={setFilterFund}
              aria-label={t(msg`Fund`)}
              data-testid="finance-filter-fund"
            />
          </Field>
          <Field id="fin-cat" label={t(msg`Category`)}>
            <Select
              options={[
                { value: "", label: t(msg`All categories`) },
                ...churchCategories().map((c) => ({ value: c.id, label: c.name ?? c.id })),
              ]}
              value={filterCategory()}
              onChange={setFilterCategory}
              aria-label={t(msg`Category`)}
              data-testid="finance-filter-category"
            />
          </Field>
          <div class="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("csv")}
              data-testid="finance-export-csv"
            >
              <Download size={14} /> {t(msg`CSV`)}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("pdf")}
              data-testid="finance-export-pdf"
            >
              <Printer size={14} /> {t(msg`PDF`)}
            </Button>
          </div>
        </div>

        {/* Registros */}
        <Show
          when={filtered().length > 0}
          fallback={
            <p class="text-muted-foreground" data-testid="finance-list-empty">
              {t(msg`No records in this range yet.`)}
            </p>
          }
        >
          <div class="space-y-2" data-testid="finance-list">
            <For each={filtered()}>
              {(r) => (
                <Card data-testid="finance-item" data-finance-kind={r.kind ?? "income"}>
                  <CardContent class="flex items-center gap-3 py-3">
                    <span
                      class={`flex size-8 shrink-0 items-center justify-center rounded-full ${
                        r.kind === "expense"
                          ? "bg-destructive/10 text-destructive"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      <Banknote size={16} />
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate font-medium">{r.description}</p>
                      <p class="truncate text-sm text-muted-foreground">
                        {[
                          kindLabel(r.kind),
                          (r.occurred_at ?? "").slice(0, 10),
                          fundName(r.fund_id),
                          categoryName(r.category_id),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <Show when={r.receipt_url}>
                      <a
                        href={resolveStorageUrl(r.receipt_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-muted-foreground hover:text-foreground"
                        aria-label={t(msg`Open receipt`)}
                        data-testid="finance-receipt-link"
                      >
                        <Paperclip size={16} />
                      </a>
                    </Show>
                    <p
                      class={`shrink-0 font-semibold tabular-nums ${
                        r.kind === "expense" ? "text-destructive" : "text-primary"
                      }`}
                      data-testid="finance-item-amount"
                    >
                      {r.kind === "expense" ? "−" : "+"}
                      {formatMoney(r.amount_minor ?? 0, r.currency)}
                    </p>
                    <Show when={canWrite()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        class="size-8 text-muted-foreground hover:text-destructive"
                        aria-label={t(msg`Move to trash`)}
                        data-testid="finance-item-trash"
                        onClick={() => void trashRecord(r.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </Show>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>

        {/* Desglose por fondo (reporte) */}
        <Show when={summary().byFund.some((b) => b.key !== "")}>
          <Card>
            <CardContent class="space-y-1 py-3" data-testid="finance-by-fund">
              <p class="text-xs font-bold uppercase tracking-wider opacity-60">{t(msg`By fund`)}</p>
              <For each={summary().byFund}>
                {(b) => (
                  <div class="flex items-center justify-between text-sm">
                    <span>{b.key ? fundName(b.key) || b.key : t(msg`No fund`)}</span>
                    <span class="tabular-nums">{formatMoney(b.netMinor, mainCurrency())}</span>
                  </div>
                )}
              </For>
            </CardContent>
          </Card>
        </Show>
      </Show>

      {/* Diálogo: nuevo registro */}
      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={t(msg`New record`)}
        icon={<Banknote size={20} />}
        submitTestId="finance-submit"
        disabled={amountInvalid()}
        onSubmit={createRecord}
      >
        <div class="grid grid-cols-2 gap-3">
          <Field id="fr-kind" label={t(msg`Type`)}>
            <Select
              options={[
                { value: "income", label: t(msg`Income`) },
                { value: "expense", label: t(msg`Expense`) },
              ]}
              value={kind()}
              onChange={setKind}
              aria-label={t(msg`Type`)}
              data-testid="finance-kind-select"
            />
          </Field>
          <Field
            id="fr-date"
            label={t(msg`Date`)}
            type="date"
            value={occurredAt()}
            onInput={setOccurredAt}
            testId="finance-date-input"
          />
        </div>
        <Field
          id="fr-description"
          label={t(msg`Description`)}
          value={description()}
          onInput={setDescription}
          placeholder={t(msg`Sunday offering, tithe, electricity bill…`)}
          testId="finance-description-input"
        />
        <div class="grid grid-cols-2 gap-3">
          <div>
            <Field
              id="fr-amount"
              label={t(msg`Amount`)}
              value={amount()}
              onInput={setAmount}
              placeholder="120.50"
              testId="finance-amount-input"
            />
            <Show when={amountInvalid()}>
              <p class="mt-1 text-xs text-destructive" data-testid="finance-amount-error">
                {t(msg`Invalid amount for this currency`)}
              </p>
            </Show>
          </div>
          <Field
            id="fr-currency"
            label={t(msg`Currency`)}
            value={currency()}
            onInput={setCurrency}
            testId="finance-currency-input"
          />
        </div>
        <div class="grid grid-cols-2 gap-3">
          <Field id="fr-fund" label={t(msg`Fund`)}>
            <Select
              options={[
                { value: "", label: t(msg`No fund`) },
                ...churchFunds().map((f) => ({ value: f.id, label: f.name ?? f.id })),
              ]}
              value={fundId()}
              onChange={setFundId}
              aria-label={t(msg`Fund`)}
              data-testid="finance-fund-select"
            />
          </Field>
          <Field id="fr-category" label={t(msg`Category`)}>
            <Select
              options={[
                { value: "", label: t(msg`No category`) },
                ...churchCategories().map((c) => ({ value: c.id, label: c.name ?? c.id })),
              ]}
              value={categoryId()}
              onChange={setCategoryId}
              aria-label={t(msg`Category`)}
              data-testid="finance-category-select"
            />
          </Field>
        </div>
        <div class="flex items-end gap-2">
          <div class="flex-1">
            <UploadField
              id="fr-receipt"
              label={t(msg`Receipt (optional)`)}
              accept="image/*,application/pdf"
              testId="finance-receipt-input"
              onUploaded={setReceiptUrl}
            />
          </div>
          <Show when={receiptUrl()}>
            <FileText
              size={16}
              class="mb-2 shrink-0 text-primary"
              data-testid="finance-receipt-ready"
            />
          </Show>
        </div>
        <Show when={amountMinor() !== null}>
          <p class="text-xs text-muted-foreground">
            {t(msg`Stored as minor units:`)} {amountMinor()} (
            {minorToDecimal(amountMinor() ?? 0, currency())})
          </p>
        </Show>
      </FormDialog>

      {/* Diálogo: nuevo fondo */}
      <FormDialog
        open={fundOpen()}
        onOpenChange={setFundOpen}
        title={t(msg`New fund`)}
        submitTestId="fund-submit"
        onSubmit={createFund}
      >
        <Field
          id="fund-name"
          label={t(msg`Name`)}
          value={fundNameInput()}
          onInput={setFundNameInput}
          placeholder={t(msg`General, Building, Missions…`)}
          testId="fund-name-input"
        />
      </FormDialog>

      {/* Diálogo: nueva categoría */}
      <FormDialog
        open={categoryOpen()}
        onOpenChange={setCategoryOpen}
        title={t(msg`New category`)}
        submitTestId="finance-category-submit"
        onSubmit={createCategory}
      >
        <Field
          id="fc-name"
          label={t(msg`Name`)}
          value={categoryNameInput()}
          onInput={setCategoryNameInput}
          placeholder={t(msg`Offering, tithe, utilities…`)}
          testId="finance-category-name-input"
        />
        <Field id="fc-kind" label={t(msg`Applies to`)}>
          <Select
            options={[
              { value: "both", label: t(msg`Income and expenses`) },
              { value: "income", label: t(msg`Income`) },
              { value: "expense", label: t(msg`Expenses`) },
            ]}
            value={categoryKind()}
            onChange={setCategoryKind}
            aria-label={t(msg`Applies to`)}
          />
        </Field>
      </FormDialog>
    </div>
  );
}
